import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { throttle } from 'lodash';
import { useIntl } from 'react-intl';

import { useListMonitoringGateways } from 'src/features/monitoring/api/useListMonitoringGateways';
import GatewayActionModal from 'src/features/monitoring/components/GatewayActionModal';
import SingleMonitoringGateway from 'src/features/monitoring/components/SingleMonitoringGateway';
import { GatewayState } from 'src/features/monitoring/types';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { GatewayActionState, WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { SectionBlock, SectionBlockBody } from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import MonitoringTabRow from 'src/features/monitoring/components/MonitoringTabRow';

export default function MonitoringGateways() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isOpen, setIsOpen] = React.useState(true);
    const [visitorKeyPairs, setVisitorKeyPairs] = React.useState<{
        [key: string]: {
            currentLicensePlate?: string;
            pastVisitorId?: string;
        };
    }>({});
    const [gatewayStates, setGatewayStates] = React.useState<{ [key: string]: GatewayState | null }>({});
    const [gatewayAction, setGatewayAction] = React.useState<GatewayActionState>(null);
    // Busy/free for SENSOR gateways. `gatewaySensorBusy` holds the backend-computed aggregate when an
    // event carries `is_busy`; `gatewaySensorStates` tracks each sensor's last activated state so we can
    // aggregate (busy = any sensor activated) ourselves when the event omits `is_busy`. SENSORLESS
    // gateways instead use `lprBusyKeyPairs` (busy on an LPR detection, free once its pass registers).
    const [gatewaySensorBusy, setGatewaySensorBusy] = React.useState<{ [key: string]: boolean }>({});
    const [gatewaySensorStates, setGatewaySensorStates] = React.useState<{
        [key: string]: { [sensorId: string]: boolean };
    }>({});
    const [lprBusyKeyPairs, setLprBusyKeyPairs] = React.useState<{ [key: string]: boolean }>({});

    const { data, isLoading } = useListMonitoringGateways({ parkingId });
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    // Seed the WS-driven state for gateways the first time we see them in the API response,
    // so the UI shows the persisted current/last visitor and opened/closed state on first
    // load instead of waiting for a WS event. Subsequent re-fetches don't overwrite live
    // updates because we only seed keys that aren't already in local state.
    React.useEffect(() => {
        if (!data) return;
        setVisitorKeyPairs((prev) => {
            const next = { ...prev };
            data.results.forEach((gateway) => {
                if (next[gateway.id]) return;
                next[gateway.id] = {
                    currentLicensePlate: gateway.current_visitor?.license_plate,
                    pastVisitorId: gateway.last_visitor_today?.id,
                };
            });
            return next;
        });
        setGatewayStates((prev) => {
            const next = { ...prev };
            data.results.forEach((gateway) => {
                if (gateway.id in next) return;
                next[gateway.id] = gateway.state;
            });
            return next;
        });
        setLprBusyKeyPairs((prev) => {
            const next = { ...prev };
            data.results.forEach((gateway) => {
                if (gateway.id in next) return;
                next[gateway.id] = gateway.is_busy;
            });
            return next;
        });
    }, [data]);

    const handleWebSocket = React.useMemo(
        () =>
            throttle((event: WebSocketEvent) => {
                if (
                    event.message === WebSocketEventMessages.NEW_ENTRY_PASS ||
                    event.message === WebSocketEventMessages.NEW_EXIT_PASS
                ) {
                    setVisitorKeyPairs((prevState) => ({
                        ...prevState,
                        [event.data.gateway_id]: {
                            pastVisitorId: event.data.visitor_id,
                        },
                    }));
                    // Sensorless gateways read busy from here; the registered pass means the vehicle left -> free.
                    setLprBusyKeyPairs((prevState) => ({ ...prevState, [event.data.gateway_id]: false }));
                } else if (event.message === WebSocketEventMessages.NEW_LPR_DETECTION) {
                    setVisitorKeyPairs((prevState) => ({
                        ...prevState,
                        [event.data.gateway_id]: {
                            ...prevState[event.data.gateway_id],
                            currentLicensePlate: event.data.license_plate,
                        },
                    }));
                    // Sensorless gateways read busy from here; a detection waiting for its pass -> busy.
                    setLprBusyKeyPairs((prevState) => ({ ...prevState, [event.data.gateway_id]: true }));
                } else if (event.message === WebSocketEventMessages.GATEWAY_STATE_CHANGED) {
                    setGatewayStates((prevState) => ({
                        ...prevState,
                        [event.data.gateway_id]: event.data.state,
                    }));
                } else if (event.message === WebSocketEventMessages.GATEWAY_SENSOR_STATE_CHANGED) {
                    const { gateway_id, is_busy, sensor } = event.data;
                    // Prefer the backend-computed aggregate when present.
                    if (typeof is_busy === 'boolean') {
                        setGatewaySensorBusy((prevState) => ({ ...prevState, [gateway_id]: is_busy }));
                    }
                    // Always record this sensor's level so we can aggregate (busy = any sensor activated)
                    // ourselves when the event doesn't carry is_busy.
                    if (sensor) {
                        setGatewaySensorStates((prevState) => ({
                            ...prevState,
                            [gateway_id]: { ...(prevState[gateway_id] ?? {}), [sensor.id]: sensor.activated },
                        }));
                    }
                }
            }, 500),
        [],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
    }, [handleWebSocket, subscribe, unsubscribe]);

    return (
        <>
            <GatewayActionModal gatewayAction={gatewayAction} setGatewayAction={setGatewayAction} />
            <div className="flex items-center justify-between">
                <MonitoringTabRow />
                <div className="flex justify-end">
                    <Button size="mdIcon" variant="outline" onClick={() => setIsOpen((prevState) => !prevState)}>
                        <FontAwesomeIcon icon={isOpen ? ICON_MAP.COMPRESS : ICON_MAP.EXPAND} />
                        <span className="hidden md:block">
                            {formatMessage(
                                isOpen ? parkingStatusMessages.collapseAll : parkingStatusMessages.expandAll,
                            )}
                        </span>
                    </Button>
                </div>
            </div>
            <SectionBlock>
                <SectionBlockBody className="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="h-160 rounded-xl" />
                        ))}
                    {data?.results.map((gateway) => {
                        const sensors = gatewaySensorStates[gateway.id];
                        // A sensor edge or an is_busy aggregate has arrived: drive the badge from the sensors.
                        const hasSensorSignal =
                            gatewaySensorBusy[gateway.id] !== undefined ||
                            Boolean(sensors && Object.keys(sensors).length);
                        const isBusy = hasSensorSignal
                            ? (gatewaySensorBusy[gateway.id] ?? Object.values(sensors ?? {}).some(Boolean))
                            : gateway.has_sensors
                              ? gateway.is_busy
                              : (lprBusyKeyPairs[gateway.id] ?? gateway.is_busy);

                        return (
                            <SingleMonitoringGateway
                                key={gateway.id}
                                gateway={gateway}
                                isOpen={isOpen}
                                isBusy={isBusy}
                                visitors={visitorKeyPairs[gateway.id]}
                                currentState={gatewayStates[gateway.id] ?? gateway.state}
                                setGatewayAction={setGatewayAction}
                            />
                        );
                    })}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
}
